import { getApiBaseUrl } from '../api/apiBase';

/**
 * Debug logger - records UI interactions and API calls for debugging.
 * Enable with REACT_APP_DEBUG=true in .env.development
 * Export to CSV via window.__debugExportCSV() or DebugPanel
 * 
 * Session-based: All logs for a session go to one file (debug-session-YYYYMMDD-HHMMSS.csv)
 * Auto-saves every 30 seconds and on page unload/HMR
 */
// Enable only when REACT_APP_DEBUG=true (do not auto-enable on localhost — kills lag)
const DEBUG = process.env.REACT_APP_DEBUG === 'true';

const entries = [];
let lastSavedIndex = 0;

// Session ID: NEW id per page load (never reuse from storage). Prevents overwriting prev session file on reload.
const SESSION_ID = typeof window !== 'undefined' 
  ? `session-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}`
  : null;

function log(type, details = {}) {
  if (!DEBUG) return;
  const row = {
    timestamp: new Date().toISOString(),
    type,
    ...details,
  };
  entries.push(row);
  if (typeof console !== 'undefined' && console.log) {
    console.log('[debug]', type, details);
  }
  if (typeof window !== 'undefined' && window.__debugLog) {
    window.__debugLog(row);
  }
}

function escapeCsv(val) {
  if (val == null) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function toCsvRow(obj) {
  const headers = ['timestamp', 'type', 'action', 'url', 'method', 'status', 'duration_ms', 'details'];
  return headers.map((h) => escapeCsv(obj[h])).join(',');
}

export function debugLog(type, details = {}) {
  log(type, details);
}

export function debugApiRequest(url, method = 'GET', body) {
  log('api_request', {
    action: 'request',
    url,
    method,
    details: body ? JSON.stringify(body).slice(0, 200) : '',
  });
}

export function debugApiResponse(url, method, status, durationMs, error, responseBody) {
  const isSlow = durationMs > 1000;
  const details = error
    ? `${durationMs}ms | ERR: ${String(error).slice(0, 150)}`
    : `${durationMs}ms${isSlow ? ' [SLOW]' : ''} | ${(responseBody || '').slice(0, 150)}`;
  log('api_response', {
    action: 'response',
    url,
    method,
    status: status ?? (error ? 'ERR' : ''),
    duration_ms: durationMs,
    details,
  });
}

export function debugClick(element, context) {
  log('click', {
    action: 'click',
    details: context || (element?.id || element?.className || element?.tagName || 'unknown'),
  });
}

export function debugNav(from, to) {
  log('nav', {
    action: 'navigation',
    details: `${from || '?'} → ${to || '?'}`,
  });
}

export function debugAuth(action, details) {
  log('auth', {
    action,
    details: typeof details === 'string' ? details : JSON.stringify(details).slice(0, 200),
  });
}

export function debugError(context, error) {
  log('error', {
    action: 'error',
    details: `${context}: ${error?.message || error}`,
  });
}

export function debugPerf(component, action, durationMs) {
  const isSlow = durationMs > 100;
  log('perf', {
    action,
    duration_ms: durationMs,
    details: `${component}: ${durationMs}ms${isSlow ? ' [SLOW]' : ''}`,
  });
}

export function exportToCsv() {
  const header = 'timestamp,type,action,url,method,status,duration_ms,details';
  const rows = entries.map((e) => toCsvRow(e));
  return [header, ...rows].join('\n');
}

export function downloadCsv(filename) {
  const csv = exportToCsv();
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename || `debug-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function getEntries() {
  return [...entries];
}

export function clearEntries() {
  entries.length = 0;
}

export function isDebugEnabled() {
  return DEBUG;
}

export function openDebugPanel() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('kuroco:open-debug-panel'));
  }
}

/** Export only new entries since last save (for incremental append) */
function exportNewEntriesToCsv() {
  const header = 'timestamp,type,action,url,method,status,duration_ms,details';
  const newEntries = entries.slice(lastSavedIndex);
  if (newEntries.length === 0) return null;
  const rows = newEntries.map((e) => toCsvRow(e));
  const isFirst = lastSavedIndex === 0;
  return isFirst ? [header, ...rows].join('\n') : [header, ...rows].join('\n');
}

/** Auto-export CSV to backend debug_output/ folder (localhost only) */
export async function autoExportToBackend(force = false) {
  if (entries.length === 0) return;
  if (!force && entries.length === lastSavedIndex) return;
  
  const apiBase =
    process.env.REACT_APP_API_URL ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:8000/api/v1'
      : null);
  if (!apiBase) return;
  
  try {
    const isAppend = lastSavedIndex > 0;
    const csv = exportNewEntriesToCsv();
    if (!csv) return;
    
    const res = await fetch(`${apiBase.replace(/\/$/, '')}/debug/export-csv`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ csv, session_id: SESSION_ID, append: isAppend }),
    });
    if (res.ok) {
      const data = await res.json();
      const savedCount = entries.length - lastSavedIndex;
      lastSavedIndex = entries.length;
      console.log(`[debug] Saved ${savedCount} entries to ${data.path}`);
    }
  } catch (e) {
    console.warn('[debug] Auto-export failed:', e);
  }
}

/** Sync beacon export (for beforeunload) */
function beaconExport() {
  if (entries.length === 0 || entries.length === lastSavedIndex) return;
  const csv = exportNewEntriesToCsv();
  if (!csv) return;
  const body = JSON.stringify({ csv, session_id: SESSION_ID, append: lastSavedIndex > 0 });
  const base = getApiBaseUrl();
  try {
    const u = new URL(base);
    if (u.hostname !== 'localhost' && u.hostname !== '127.0.0.1') return;
  } catch {
    return;
  }
  navigator.sendBeacon?.(
    `${base.replace(/\/$/, '')}/debug/export-csv`,
    new Blob([body], { type: 'application/json' })
  );
}

// Expose for console / DebugPanel
if (typeof window !== 'undefined') {
  window.__debugExportCSV = exportToCsv;
  window.__debugDownloadCSV = downloadCsv;
  window.__debugAutoExportToBackend = autoExportToBackend;
  window.__debugGetEntries = getEntries;
  window.__debugClear = clearEntries;
  window.__debugEnabled = isDebugEnabled;
  window.__debugSessionId = SESSION_ID;

  if (DEBUG && window.location.hostname === 'localhost') {
    // Auto-save every 30 seconds
    setInterval(() => {
      if (entries.length > lastSavedIndex) {
        autoExportToBackend();
      }
    }, 30000);

    // Save on page unload (close tab, refresh, navigate away)
    window.addEventListener('beforeunload', beaconExport);

    // Save on visibility change (tab switch, minimize)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        beaconExport();
      }
    });

    // HMR support: save before hot reload (webpack/vite)
    if (module.hot) {
      module.hot.dispose(() => {
        beaconExport();
        console.log('[debug] Saved before HMR reload');
      });
    }

    // Log session start
    console.log(`[debug] Session: ${SESSION_ID}`);
  }
}
